/**
 * Forced password change after temp password (forgot-password or colleague reset).
 */
import React, { useState } from 'react';
import './login.css';
import AdminAuthShell from './AdminAuthShell';
import { changeAdminPassword } from '../../../services/adminAuthApi';
import {
  getAdminEmail,
  markAdminPasswordChanged,
  notifyAdminAuthChanged,
} from '../auth/adminAuthStorage';

type AdminChangePasswordProps = {
  onLogout: () => void;
};

export default function AdminChangePassword({ onLogout }: AdminChangePasswordProps) {
  const email = getAdminEmail() || '';
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    if (newPassword.length < 8) {
      setError('新しいパスワードは8文字以上にしてください。');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('確認用パスワードが一致しません。');
      return;
    }
    if (newPassword === currentPassword) {
      setError('新しいパスワードは現在のパスワードと異なるものにしてください。');
      return;
    }
    setSubmitting(true);
    try {
      await changeAdminPassword(currentPassword, newPassword);
      markAdminPasswordChanged();
      notifyAdminAuthChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'パスワードの変更に失敗しました。');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AdminAuthShell userName={email || '管理者'} onLogout={onLogout}>
      <form className="admin-login__form" onSubmit={handleSubmit}>
        <p className="admin-login__hint">
          仮パスワードでログインしました。続ける前に、新しいパスワードを設定してください。
        </p>
        <input
          type="password"
          className="admin-login__input"
          placeholder="現在のパスワード（仮パスワード）"
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          autoComplete="current-password"
          required
        />
        <input
          type="password"
          className="admin-login__input"
          placeholder="新しいパスワード（8文字以上）"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          autoComplete="new-password"
          required
          minLength={8}
        />
        <input
          type="password"
          className="admin-login__input"
          placeholder="新しいパスワード（確認）"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          autoComplete="new-password"
          required
          minLength={8}
        />
        {error && <p className="admin-login__error" role="alert">{error}</p>}
        <button type="submit" className="admin-login__btn" disabled={submitting}>
          {submitting ? '変更中…' : 'パスワードを変更'}
        </button>
      </form>
    </AdminAuthShell>
  );
}
